import {
  colorDarkExpo,
  lightbackground2,
  textactive2,
} from "@/_core/_helpers/_parametric_tools";
import { HoverElement } from "./HoverElement";
import { select, format, timeFormat } from "d3";
import { lightBackground } from "../../_helpers/_parametric_tools";

export class TooltipHover extends HoverElement {
  constructor(params) {
    super({ ...params });
    // Local Attributes
    this.type = params.type;
    this.currencyFormat = params.currencyFormat;
    this.valueLast = params.valueLast;
    this.xTooltipWidth = params.xTooltipWidth ? params.xTooltipWidth : 80;
    this.yTooltipWidth = params.yTooltipWidth ? params.yTooltipWidth : 60;
    this.create({ ...params });
  }

  recalculate() {
    // last rate
    let yMax = this.svg.y.invert(0);
    let yMin = this.svg.y.invert(this.svg.height);

    select("." + this.className)
      .transition()
      .duration(500)
      .style(
        "opacity",
        yMin <= this.valueLast && this.valueLast <= yMax ? 1 : 0,
      )
      .style(
        "top",
        this.svg.y(this.valueLast) + this.svg.margin.top - 11 + "px",
      );
  }

  show({
    yPixels,
    selData,
    dateFormat,
    xt,
    formatyInside,
    datayInside = selData,
    date = "date",
    textField = "date",
    calcPos = false,
    yPixelsInside = false,
    isLeftY = true,
  }) {
    let d3Element = select("." + this.className);
    switch (this.type) {
      case "y": {
        let yRate = this.svg.y.invert(yPixels);
        const setTooltipPosition = (leftPosition) => {
          d3Element
            .style("opacity", 1)
            .style("left", leftPosition + "px")
            .style("top", yPixels + this.svg.margin.top - 11 + "px")
            .html(format(this.currencyFormat)(yRate));
        };

        if (isLeftY) {
          setTooltipPosition(this.svg.width + this.svg.margin.left + 1);
        } else {
          setTooltipPosition(1);
        }
        break;
      }
      case "y-inside": {
        const { posAu, posTop, posBottom } = this.calculatePositionTooltip({
          yPixels,
          selData,
          date,
          calcPos,
          yPixelsInside,
        });

        const text = formatyInside
          ? formatyInside(datayInside[textField])
          : textField;
        const leftTooltWidth = `${this.svg.x(datayInside[date]) -
          this.yInsideTooltipWidth +
          40}px`;
        const left = formatyInside
          ? `${this.svg.margin.left +
              this.svg.x(datayInside[date]) +
              this.svg.x.bandwidth() / 2 -
              posAu}px`
          : leftTooltWidth;

        d3Element
          .style("opacity", 1)
          .style("left", left)
          .style("top", posTop)
          .style("bottom", posBottom)
          .html(text);
        break;
      }
      case "y2-2axis": {
        const y2Rate = this.svg.y2.invert(yPixels);
        if (!Number.isNaN(y2Rate)) {
          d3Element
            .style("opacity", 1)
            .style("left", this.svg.width + this.svg.margin.left + 2 + "px")
            .style("top", yPixels + this.svg.margin.top - 11 + "px")
            .html(format(this.currencyFormat)(y2Rate));
        }
        break;
      }
      case "y1-2axis": {
        let yRate = this.svg.y.invert(yPixels);
        d3Element
          .style("opacity", 1)
          .style("left", 4 + "px")
          .style("top", yPixels + this.svg.margin.top - 11 + "px")
          .html(format(this.currencyFormat)(yRate));
        break;
      }
      case "x":
        d3Element
          .style("opacity", 1)
          .style(
            "left",
            xt(selData[date]) +
              this.svg.margin.left -
              this.xTooltipWidth / 2 +
              "px",
          )
          .style("top", this.svg.height + this.svg.margin.top + 2 + "px")
          .html(
            date.includes("date")
              ? timeFormat(dateFormat)(selData[date])
              : selData[date],
          );
        break;
    }
  }

  hide() {
    let d3Element = select("." + this.className);
    d3Element
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
  }

  create({
    colorTheme,
    colorXTooltip = colorDarkExpo,
    colorYTooltip = lightbackground2,
    colorLastYTooltip = colorDarkExpo,
    colorInsideYTooltip = lightBackground,
    colorY2AxisTooltip = colorDarkExpo,
    isLeft = false,
    yTooltipWidth = 60,
    yInsideTooltipWidth = 60,
  }) {
    switch (this.type) {
      // Tooltip of the last value in Y Axis
      case "last-y": {
        this.className = "last-y-tooltip-" + this.name;
        const position = isLeft ? "right" : "left";
        const positionPx = isLeft
          ? `${this.svg.width + this.svg.margin.right + 1}px`
          : `${this.svg.width + this.svg.margin.left + 1}px`;
        const topPx =
          this.svg.y(this.valueLast) + this.svg.margin.top - 11 + "px";

        this.baseTooltip({
          colorTheme,
          bckColorTooltip: colorLastYTooltip,
          className: "last-y-tooltip-",
          opacity: 1,
          widthTooltip: yTooltipWidth,
          content: format(this.currencyFormat)(this.valueLast),
          positionStatic: {
            position,
            positionPx,
            top: "top",
            topPx,
          },
        });
        break;
      }
      // Y Tooltip on hover
      case "y":
        this.baseTooltip({
          colorTheme,
          bckColorTooltip: colorYTooltip,
          className: "y-tooltip-",
          widthTooltip: yTooltipWidth,
        });
        break;
      // Y Tooltip inside chart on Hover
      case "y-inside":
        this.yInsideTooltipWidth = yInsideTooltipWidth;
        this.baseTooltip({
          colorTheme,
          bckColorTooltip: colorInsideYTooltip,
          className: "y-inside-tooltip-",
          widthTooltip: yInsideTooltipWidth,
        });
        break;
      // Y1 Tooltip 2 axis
      case "y1-2axis":
        this.baseTooltip({
          colorTheme,
          bckColorTooltip: colorYTooltip,
          className: "y1-2axis-tooltip-",
          widthTooltip: yTooltipWidth,
        });
        break;
      // Y2 Tooltip 2 axis
      case "y2-2axis":
        this.baseTooltip({
          colorTheme,
          bckColorTooltip: colorY2AxisTooltip,
          className: "y2-2axis-tooltip-",
          widthTooltip: yTooltipWidth,
        });
        break;
      // X Tooltip on Hover
      case "x":
        this.baseTooltip({
          colorTheme,
          bckColorTooltip: colorXTooltip,
          className: "x-tooltip-",
          widthTooltip: this.xTooltipWidth,
        });
        break;
    }
  }

  baseTooltip({
    colorTheme,
    bckColorTooltip = colorDarkExpo,
    bdrColorTooltip = bckColorTooltip,
    className = "",
    opacity = 0,
    widthTooltip = 60,
    content = "",
    positionStatic = {},
  }) {
    this.className = `${className}${this.name}`;
    select(`#${this.svg.parentElement}`)
      .append("div")
      .style("opacity", opacity)
      .style("margin", "0 auto")
      .style("text-align", "center")
      .style("background", bckColorTooltip(colorTheme))
      .style("border", bdrColorTooltip(colorTheme))
      .style("color", textactive2(colorTheme))
      .style("padding", "2px")
      .attr("class", `tooltip ${this.className}`)
      .style("font-size", "0.8rem")
      .style("width", `${widthTooltip}px`)
      .style(positionStatic?.position, positionStatic?.positionPx)
      .style(positionStatic?.top, positionStatic?.topPx)
      .html(content);
  }

  calculatePositionTooltip({
    yPixels,
    selData,
    date = "date",
    calcPos = false,
    yPixelsInside = false,
  }) {
    let posAu =
      this.svg.x(selData[date]) > 0.45 * this.svg.width
        ? this.yInsideTooltipWidth + 5
        : -5;
    let scrnHght = document.getElementById("whole-page-div").clientHeight;
    let posTop, posBottom;
    let heightScreen =
      this.svg.height > 0.5 * scrnHght
        ? yPixels < 0.45 * this.svg.height
        : false;
    const isYPixelsInside = yPixelsInside ? yPixelsInside : yPixels;
    if (heightScreen || calcPos) {
      posTop = isYPixelsInside + this.svg.margin.top + 10 + "px";
      posBottom = null;
    } else {
      posTop = null;
      posBottom =
        this.svg.height + this.svg.margin.bottom - isYPixelsInside + 10 + "px";
    }

    return { posAu, posTop, posBottom };
  }
}
